import {
    Multiselect,
    MultiselectProps,
    Select,
    SelectProps
} from '@cloudscape-design/components'; // Import both components
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalState } from '../../pages/context/GlobalStateContext';

// --- CORE TYPES ---
type ItemBookingType = {
    idBookingType: number;
    bookingType: string;
    showOnBooking: number;
    showOnFlightLog: number;
    value: string; // Cloudscape value (string ID)
    label: string; // Cloudscape label
};

// --- PROPS INTERFACES (Strictly following the reference pattern) ---

// When single = true (Single Selection)
interface SelectFlightTypeSingleProps {
    single: true;
    value?: number; // Single ID
    width?: number;
    // Returns ID or null
    onSelect?: (selected: number | null) => void;
    visible?: boolean;
    // Token/Multi props are irrelevant here but kept for consistency if needed later
    readonly?: boolean;
    tokenLimit?: number;
    showOnBooking?: number;
    showOnFlightLog?: number;
    hideTokens?: boolean;
    dataset?: any[];
}

// When single = false (or undefined) (Multi Selection)
interface SelectFlightTypeMultiProps {
    single?: false; // default is "false"
    value?: number[]; // Array of IDs
    width?: number;
    // Returns array of IDs
    onSelect?: (selected: number[]) => void;
    visible?: boolean;
    readonly?: boolean;
    tokenLimit?: number;
    showOnBooking?: number

    ;
    showOnFlightLog?: number;
    hideTokens?: boolean;
    dataset?: any[];
}

export type SelectFlightTypeProps =
    | SelectFlightTypeSingleProps
    | SelectFlightTypeMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectFlightType: React.FC<SelectFlightTypeProps> = (props) => {
    const {
        single = true,
        value,
        onSelect,
        width = 200,
        readonly,
        tokenLimit = 3,
        hideTokens = false,
        showOnBooking = -1,
        showOnFlightLog = -1,
        visible // Currently unused, but kept for completeness
    } = props;

    const { data } = useGlobalState();
    if (!data || !data.bookingTypes) return <></>;

    // State for Single Selection (holds the selected object)
    const [selectedSingle, setSelectedSingle] = useState<ItemBookingType | null>(null);

    // State for Multi Selection (holds the array of selected objects)
    const [selectedMulti, setSelectedMulti] = useState<ItemBookingType[]>([]);


    const localDataSet = props.dataset || data.bookingTypes; 
    // --- Mapped Booking Types (Options List) ---
    const mappedBookingTypes: ItemBookingType[] = useMemo(() => {
        
        let types: ItemBookingType[] = localDataSet
            .filter((plan: ItemBookingType) => {
                // If showOnFlightLog is -1, it returns true (bypassing the filter).
                // Otherwise, it checks if plan.showOnFlightLog is 1.
                const isFlightLogMatch = showOnFlightLog === -1 || plan.showOnFlightLog === showOnFlightLog;

                // If showOnBooking is -1, it returns true.
                // Otherwise, it checks if plan.showOnBooking is 1.
                const isOnBookingMatch = showOnBooking === -1 || plan.showOnBooking === showOnBooking;
                return isFlightLogMatch && isOnBookingMatch;
            })
            .map((plan: ItemBookingType) => ({
                idBookingType: plan.idBookingType,
                bookingType: plan.bookingType,
                showOnBooking: plan.showOnBooking,
                showOnFlightLog: plan.showOnFlightLog,
                value: plan.idBookingType.toString(),
                label: plan.bookingType
            }));

        // The placeholder option "Seleccione un tipo" should only be added 
        // for the single-select mode.
        if (single) {
            types.unshift({
                idBookingType: 0,
                bookingType: "Seleccione un tipo",
                showOnBooking: 0,
                showOnFlightLog: 1, // Must be 1 to pass the initial filter logic
                value: "0",
                label: "Seleccione un tipo"
            });
        }
        return types;
    }, [data.bookingTypes, single]);


    // --- Initialization & Value Sync Effect ---
    useEffect(() => {
        // Clear state if value is falsy
        try {
            if (!value) {
                setSelectedSingle(null);
                setSelectedMulti([]);
                return;
            }

            if (single) {
                // SINGLE MODE: Value is a number (ID)
                const id = value as number;
                const match = mappedBookingTypes.find(type => type.idBookingType === id) || null;
                setSelectedSingle(match);

            } else {
                // MULTI MODE:

                // 💡 FIX: Ensure 'value' is actually an array before assigning it to ids
                if (Array.isArray(value)) {
                    const ids = value as number[]; // Now safe to assert as number[]
                    const matches = mappedBookingTypes.filter(type => ids.includes(type.idBookingType));
                    setSelectedMulti(matches);
                } else {
                    // If value is passed but is NOT an array (e.g., null, 0, or an object), 
                    // treat it as an empty selection to prevent error.
                    console.error("SelectFlightType received a non-array value for multi-select mode:", value);
                    setSelectedMulti([]);
                }
            }
        } catch (error) {
            console.error("Error in SelectFlightType useEffect:", error);

        }
    }, [value, single, mappedBookingTypes]);


    // --- Single Select Handler ---
    const handleSingleChange: SelectProps['onChange'] = ({ detail }) => {
        const selectedOption = detail.selectedOption as ItemBookingType | null;

        setSelectedSingle(selectedOption);

        if (onSelect && single) {
            const selectedId = selectedOption?.idBookingType ?? null; // Extract ID
            // Assert the function signature for single mode
            (onSelect as (selected: number | null) => void)(selectedId);
        }
    };

    // --- Multi Select Handler ---
    const handleMultiChange: MultiselectProps['onChange'] = ({ detail }) => {
        const list = detail.selectedOptions as ItemBookingType[];

        // Use spread to ensure setting a new array reference
        setSelectedMulti([...list]);

        if (onSelect && !single) {
            const selectedIds = list.map(item => item.idBookingType); // Extract array of IDs
            // Assert the function signature for multi mode
            (onSelect as (selected: number[]) => void)(selectedIds);
        }
    };

    // --- Rendering ---

    // Placeholder changes based on mode
    const placeholder = single ? "Seleccionar tipo de reserva" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar tipos de reserva";

    const wrapperStyle: React.CSSProperties = {
        width: width > 0 ? `${width}px` : '100%'
    };

    return (
        <div style={wrapperStyle}>
            {single ? (
                // SINGLE SELECTION (Cloudscape Select)
                <Select
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOption={selectedSingle}
                    options={mappedBookingTypes}
                    onChange={handleSingleChange}
                />
            ) : (
                // MULTI SELECTION (Cloudscape Multiselect)
                <Multiselect
                    tokenLimit={tokenLimit}
                    hideTokens={hideTokens}
                    readOnly={readonly}
                    placeholder={placeholder}
                    selectedOptions={selectedMulti}
                    options={mappedBookingTypes}
                    onChange={handleMultiChange}
                />
            )}
        </div>
    );
};

export default SelectFlightType;